/**
 * GET /api/meta — the app's runtime identity: display names, the brand profile, the release
 * stage, where each role lands, and which features are switched on.
 *
 * It is the only public API route, so it loads before sign-in and the shell can render a
 * correctly branded sign-in screen. Nothing in web/src hardcodes an organization's name or
 * colors; they all come from here (rule 5).
 */
import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { applyBrand, brandSelector, type BrandProfile } from '@/lib/brand';
import { setDefaultCurrency } from '@/lib/money';
import { useTheme } from '@/hooks/useTheme';

export interface FeatureFlags {
  ask: boolean;
  healthCard: boolean;
  dispatch: boolean;
  ideasFromDesign: boolean;
  whatIf: boolean;
}

/** The configured fiscal calendar, camelCase on the wire and snake_case on disk. */
export interface MetaPlanningCalendar {
  fiscalStartMonth: number;
  fiscalYearLabel: 'start_year' | 'end_year';
  defaultCurrency: string;
}

/** #152: stable public URLs of the uploaded logos; null = none uploaded (the app name is shown). */
export interface MetaLogos {
  logomarkLight: string | null;
  logomarkDark: string | null;
  lockupLight: string | null;
  lockupDark: string | null;
}

export interface AppMeta {
  appName: string;
  /**
   * #596: the platform's display name (app_setting `platform_name`; the seed says the platform's
   * name, never this bundle). The wordmark and every document title carry it. Absent on an API
   * older than round 10 — the shell then shows the app name alone.
   */
  platformName?: string;
  /** R-16 T-134: the short name for the shell's labels ("Search CORE+"); the API falls back to platformName */
  platformShortName?: string;
  /** the platform's stable technical slug (decision 12) */
  platformSlug?: string;
  logos?: MetaLogos;
  orgName: string;
  brand: BrandProfile;
  /** Where a signed-in user goes from `/` when no role-specific landing applies. */
  landingRoute: string;
  /** role code → route. The meeting scope is resolved from this, never hardcoded. */
  landingByRole: Record<string, string>;
  featureFlags: FeatureFlags;
  /** Grouping only — it never moves a stored date or amount (PLANNING-CONTRACT §2). */
  planningCalendar: MetaPlanningCalendar;
  authMode: string;
  /** `alpha`, `beta`, `ga`, … — anything but `ga` shows a banner on every page. */
  releaseStage: string;
}

/**
 * What the shell uses before /api/meta answers (a few hundred ms on a cold load) and if it
 * fails. Deliberately generic: a wrong name for one frame is better than a tenant's name
 * compiled into the bundle.
 */
export const META_FALLBACK: AppMeta = {
  appName: 'Capability Roadmap',
  platformName: '',
  orgName: '',
  brand: undefined as unknown as BrandProfile,
  landingRoute: '/registry',
  landingByRole: {},
  featureFlags: { ask: false, healthCard: false, dispatch: false, ideasFromDesign: false, whatIf: false },
  planningCalendar: { fiscalStartMonth: 1, fiscalYearLabel: 'start_year', defaultCurrency: 'USD' },
  authMode: 'entra',
  releaseStage: 'ga',
};

export const metaQueryKey = ['cigs', 'meta'] as const;

const MetaContext = createContext<{ meta: AppMeta; loading: boolean }>({
  meta: META_FALLBACK,
  loading: true,
});

/**
 * Round 12 #727 (labelled shortcut, ADR-027): the four surface layers, the ink steps and the rules are
 * the CORE style guide's (src/index.css), in both modes. A brand profile from /api/meta still sets the
 * fonts, the interactive colour and the status fills; the layer roles below it may not repaint — a
 * profile's single "surface" collapsed the page and the card into one layer.
 */
export const STYLE_GUIDE_LAYERS: ReadonlySet<string> = new Set([
  '--background', '--card', '--popover', '--muted', '--secondary', '--paper-2', '--accent',
  '--muted-foreground', '--ink-3', '--border', '--rule-strong', '--input', '--soft', '--soft-foreground', '--display',
]);
export function styleGuideLayers<E extends { style: { setProperty(k: string, v: string): void; removeProperty(k: string): void } }>(el: E) {
  return {
    style: {
      setProperty: (k: string, v: string) => (STYLE_GUIDE_LAYERS.has(k) ? el.style.removeProperty(k) : el.style.setProperty(k, v)),
      removeProperty: (k: string) => el.style.removeProperty(k),
    },
  };
}

export function MetaProvider({ children }: { children: ReactNode }) {
  const { theme } = useTheme();
  const query = useQuery({
    queryKey: metaQueryKey,
    queryFn: () => apiFetch<AppMeta>('/meta'),
    // Runtime identity changes when an admin edits a setting, not while a page is open.
    staleTime: 5 * 60_000,
  });
  const meta = query.data ?? META_FALLBACK;
  // Before children render, so the first frame of every amount already uses the settings currency.
  setDefaultCurrency(meta.planningCalendar?.defaultCurrency);
  const applied = useRef<string[]>([]);

  // Brand tokens are written as inline custom properties on <html>, so they beat both
  // built-in profile blocks without index.css knowing anything about this deployment.
  useEffect(() => {
    const root = document.documentElement;
    applied.current = applyBrand(styleGuideLayers(root), meta.brand, theme, applied.current);
  }, [meta.brand, theme]);

  useEffect(() => {
    const selector = brandSelector((meta.brand as { key?: string } | undefined)?.key);
    const root = document.documentElement;
    if (selector) root.dataset.brand = selector;
    else delete root.dataset.brand;
  }, [meta.brand]);

  // R11 #649: the shell sets document.title per page (platform pages: "<page> · <platform>"; an
  // app's pages: "<page> · <app> · <platform>"); before the shell mounts it is the platform name.
  useEffect(() => {
    if (!document.title) document.title = documentTitle({ appName: '', platformName: meta.platformName || meta.appName });
  }, [meta]);

  return (
    <MetaContext.Provider value={{ meta, loading: query.isLoading }}>{children}</MetaContext.Provider>
  );
}

/** #596 #649: "<page> · <app> · <platform name>" — the platform name on every title, from /api/meta, never compiled in. */
export function documentTitle(meta: Pick<AppMeta, 'appName' | 'platformName'>, page?: string): string {
  const platform = meta.platformName?.trim() ?? '';
  const parts = [page, meta.appName, platform].filter((p): p is string => Boolean(p?.trim()));
  return [...new Set(parts)].join(' · ');
}

export function useMeta(): AppMeta {
  return useContext(MetaContext).meta;
}

export function useMetaLoading(): boolean {
  return useContext(MetaContext).loading;
}

/** A feature the API says is off is not rendered at all, not rendered-and-disabled. */
export function useFeature(flag: keyof FeatureFlags): boolean {
  return useMeta().featureFlags[flag] === true;
}

/**
 * The route a user lands on: their highest-priority role's landing route, else the app
 * default. Role order is the caller's — `Me.roles` arrives ordered by the API.
 */
export function landingFor(meta: AppMeta, roles: readonly string[]): string {
  for (const role of roles) {
    const route = meta.landingByRole[role];
    if (route) return route;
  }
  return meta.landingRoute || '/registry';
}
